import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './ProfilePage.module.css';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import { createSystemLoginApiClient } from '../utils/systemLoginApi';
import AddressSelector, { AddressData } from '../components/AddressSelector';

interface UserAddress {
  id?: string;
  cityId: string;
  name: string;
  address: string;
  phone: string;
  isDefault: number;
  ext?: UserAddrExtInfo;
}

interface UserAddrExtInfo {
  province: string;
  city: string;
  county: string;
  town?: string;
  details: string;
  company: string;
  apartment?: string;
  email?: string;
  position?: string;
}

interface UserProfile {
  id?: string;
  cityId: string;
  name: string;
  address: string;
  phone: string;
  email?: string;
  company?: string;
  position?: string;
}

interface AddressInfo {
  province: string;
  city: string;
  district: string;
  street: string;
  detail: string;
}

interface LocationState {
  from?: string;
  isFirstTime?: boolean;
  addrId?: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const systemLogin = useSystemLogin();
  const apiClient = createSystemLoginApiClient(systemLogin);
  
  const locationState = location.state as LocationState;
  const isFirstTime = locationState?.isFirstTime || false;
  const editingAddrId = locationState?.addrId;
  
  const [profile, setProfile] = useState<UserProfile>({
    name: '',
    phone: '',
    cityId: '1534422005300989953',
    address: '',
    email: '',
    company: '',
    position: '',
  });
  const [isEditing, setIsEditing] = useState(isFirstTime);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<UserProfile>>({});
  const [addressInfo, setAddressInfo] = useState<AddressInfo>({
    province: '',
    city: '',
    district: '',
    street: '',
    detail: ''
  });

  // 检查登录状态
  useEffect(() => {
    if (!systemLogin.isLoggedIn) {
      navigate('/login', { state: { from: '/profile' } });
      return;
    }
    
    // 加载用户信息
    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemLogin.isLoggedIn, navigate, editingAddrId]);

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      console.log('开始加载用户信息...');
      
      if (editingAddrId) {
        // 如果有指定的地址ID，直接加载该地址信息
        const result = await apiClient.safeGetUserAddressById<UserAddress>(editingAddrId);
        
        if (result.isLoginNeeded) {
          navigate('/login', { state: { from: '/profile' } });
          return;
        }
        
        if (result.error) {
          console.error('加载用户地址失败:', result.error);
          alert('加载用户信息失败：' + result.error);
          return;
        }
        
        if (result.data) {
          const addressData = result.data;
          setProfile({
            ...addressData,
            name: addressData.name || '',
            phone: addressData.phone || '',
            address: addressData.address || '',
            email: addressData.ext?.email || '',
            company: addressData.ext?.company || '',
            position: addressData.ext?.position || '',
          });
          
          // 设置地址信息
          if (addressData.ext) {
            const newAddressInfo = {
              province: addressData.ext.province || '',
              city: addressData.ext.city || '',
              district: addressData.ext.county || '',
              street: addressData.ext.town || '',
              detail: addressData.ext.details || ''
            };
            console.log('设置地址信息:', newAddressInfo);
            setAddressInfo(newAddressInfo);
          }
        }
      } else {
        // 如果是首次添加，查询用户是否已有地址信息
        const result = await apiClient.safeGetUserAddressList<UserAddress>('1534422005300989953');
        
        if (result.isLoginNeeded) {
          navigate('/login', { state: { from: '/profile' } });
          return;
        }
        
        if (result.error) {
          console.error('查询用户地址列表失败:', result.error);
          // 如果查询失败，仍然允许用户填写新信息
        } else if (result.data && result.data.length > 0) {
          // 如果已有地址信息，加载第一个地址
          const firstAddress = result.data[0];
          setProfile({
            ...firstAddress,
            name: firstAddress.name || '',
            phone: firstAddress.phone || '',
            address: firstAddress.address || '',
            email: firstAddress.ext?.email || '',
            company: firstAddress.ext?.company || '',
            position: firstAddress.ext?.position || '',
          });
          
          // 设置地址信息
          if (firstAddress.ext) {
            const newAddressInfo = {
              province: firstAddress.ext.province || '',
              city: firstAddress.ext.city || '',
              district: firstAddress.ext.county || '',
              street: firstAddress.ext.town || '',
              detail: firstAddress.ext.details || ''
            };
            console.log('设置首个地址信息:', newAddressInfo);
            setAddressInfo(newAddressInfo);
          }
        }
      }
    } catch (error) {
      console.error('加载用户信息异常:', error);
      alert('加载用户信息失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UserProfile> = {
    };

    // 验证用户名
    if (!profile.name.trim()) {
      newErrors.name = '请输入用户名';
    } else if (profile.name.length < 2) {
      newErrors.name = '用户名至少2个字符';
    }

    // 验证手机号
    if (!profile.phone.trim()) {
      newErrors.phone = '请输入手机号';
    } else if (!/^1[3-9]\d{9}$/.test(profile.phone)) {
      newErrors.phone = '请输入正确的手机号';
    }

      // 验证地址
      if (!addressInfo.province || !addressInfo.city || !addressInfo.district) {
        newErrors.address = '请选择完整的省市区';
      } else if (!addressInfo.street) {
        newErrors.address = '请选择街道';
      } else if (!addressInfo.detail.trim()) {
        newErrors.address = '请输入详细地址';
      } else if (addressInfo.detail.length < 5) {
        newErrors.address = '详细地址至少5个字符';
      }

    // 验证公司信息
    if (!profile.company) {
      newErrors.company = '请输入公司名称';
    }
    
    // 验证邮箱（可选）
    if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      newErrors.email = '请输入正确的邮箱地址';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      
      // 组合完整地址
      const fullAddress = `${addressInfo.province}${addressInfo.city}${addressInfo.district}${addressInfo.street}${addressInfo.detail}`.trim();
      
        // 准备提交的数据
        const addressData: UserAddress = {
          ...profile,
          cityId: profile.cityId || '1534422005300989953',
          isDefault: 0,
          address: fullAddress,
          ext: {
            ...addressInfo,
            province: addressInfo.province,
            city: addressInfo.city,
            county: addressInfo.district,
            town: addressInfo.street,
            details: addressInfo.detail,
            email: profile.email || '',
            company: profile.company || '',
            position: profile.position || ''
          }
        };
      
      let result;
      if (profile.id || editingAddrId) {
        // 更新现有地址
        result = await apiClient.safeUpdateUserAddress<UserAddress>(addressData);
      } else {
        // 添加新地址
        result = await apiClient.safeAddUserAddress<UserAddress>(addressData);
      }
      
      if (result.isLoginNeeded) {
        navigate('/login', { state: { from: '/profile' } });
        return;
      }
      
      if (result.error) {
        console.error('保存用户信息失败:', result.error);
        alert('保存失败：' + result.error);
        return;
      }
      
      if (result.data) {
        // 更新本地状态
        setProfile(result.data);
        setIsEditing(false);
        alert(profile.id || editingAddrId ? '用户信息已更新' : '用户信息已保存');
        
        // 如果是首次添加，返回首页
        if (isFirstTime) {
          navigate('/', { replace: true });
        }
      }
    } catch (error) {
      console.error('保存用户信息异常:', error);
      alert('保存失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isFirstTime) {
      // 如果是首次添加且用户取消，返回首页
      navigate('/', { replace: true });
      return;
    }
    
    setIsEditing(false);
    setErrors({});
    loadUserProfile(); // 重新加载原始数据
  };

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 清除该字段的错误
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // 处理地址变化
  const handleAddressChange = (address: AddressData) => {
    setAddressInfo({
      province: address.province,
      city: address.city,
      district: address.district,
      street: address.street,
      detail: address.detail
    });
    
    // 清除地址错误
    if (errors.address) {
      setErrors(prev => ({
        ...prev,
        address: undefined
      }));
    }
  };

  if (isLoading && !isEditing) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>{isFirstTime ? '完善用户信息' : '用户信息'}</h1>
          {!isEditing && (
            <button onClick={() => setIsEditing(true)} className={styles.editButton}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.editIcon}>
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              编辑
            </button>
          )}
        </header>

        <main className={styles.main}>
          <div className={styles.profileCard}>
            <div className={styles.avatarSection}>
              <div className={styles.avatar}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <h2 className={styles.profileTitle}>{isFirstTime ? '完善个人信息' : '个人信息'}</h2>
              <p className={styles.profileSubtitle}>{isFirstTime ? '请填写您的个人信息以便更好地为您服务' : '管理您的个人资料和账户信息'}</p>
            </div>

            <form className={styles.profileForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>用户名 *</label>
                  <div className={styles.inputWrapper}>
                    <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                      placeholder="请输入用户名"
                      disabled={!isEditing}
                    />
                  </div>
                  {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>手机号 *</label>
                  <div className={styles.inputWrapper}>
                    <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
                      placeholder="请输入手机号"
                      disabled={!isEditing}
                      maxLength={11}
                    />
                  </div>
                  {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>邮箱地址</label>
                <div className={styles.inputWrapper}>
                  <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    placeholder="请输入邮箱地址"
                    disabled={!isEditing}
                  />
                </div>
                {errors.email && <span className={styles.errorText}>{errors.email}</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>详细地址 *</label>
                <AddressSelector
                  value={addressInfo}
                  onChange={handleAddressChange}
                  disabled={!isEditing}
                   placeholder={{
                     province: '请选择省份',
                     city: '请选择城市',
                     district: '请选择区县',
                     street: '请选择街道',
                     detail: '请输入详细地址'
                   }}
                />
                {errors.address && <span className={styles.errorText}>{errors.address}</span>}
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>公司名称 *</label>
                  <div className={styles.inputWrapper}>
                    <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M3 21h18"/>
                      <path d="M5 21V7l8-4v18"/>
                      <path d="M19 21V11l-6-4"/>
                    </svg>
                    <input
                      type="text"
                      value={profile.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className={`${styles.input} ${errors.company ? styles.inputError : ''}`}
                      placeholder="请输入公司名称"
                      disabled={!isEditing}
                    />
                  </div>
                  {errors.company && <span className={styles.errorText}>{errors.company}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>职位</label>
                  <div className={styles.inputWrapper}>
                    <svg className={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    <input
                      type="text"
                      value={profile.position}
                      onChange={(e) => handleInputChange('position', e.target.value)}
                      className={styles.input}
                      placeholder="请输入职位"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className={styles.actionButtons}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelButton}
                    disabled={isLoading}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className={`${styles.saveButton} ${isLoading ? styles.loading : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className={styles.loadingSpinner}></span>
                        保存中...
                      </>
                    ) : (
                      '保存'
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;
